import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function Topbar({ currentUser, logout, themeConfig, handleThemeToggle, darkMode, canViewAsClient, viewingAsClient, onToggleClientView }) {
  const { t, i18n } = useTranslation();
  const [isIframe, setIsIframe] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Détecte si on est dans une iframe
    setIsIframe(window !== window.parent);
  }, []);

  const switchLanguage = (lang) => {
    localStorage.setItem('locale', lang);
    i18n.changeLanguage(lang);
  };

  const renderDropdownMenu = () => (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <span className="dark:text-gray-200">
          {currentUser?.username?.charAt(0).toUpperCase() + currentUser?.username?.slice(1)}
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Hack to avoid the theme toggle to be hidden when the menu is not open */}
      <div className="hidden"><ThemeToggle onToggle={handleThemeToggle} /></div>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
            <div className="py-1">
              {currentUser?.isadmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <div className="flex items-center space-x-2">
                    {t('Administration')}
                  </div>
                </Link>
              )}

              {canViewAsClient && (
                <button
                  onClick={() => { onToggleClientView(!viewingAsClient); setIsMenuOpen(false); }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {viewingAsClient ? t('Back to admin view') : t('View as client')}
                </button>
              )}

              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">{t('Theme')}</span>
                <ThemeToggle onToggle={handleThemeToggle} />
              </div>

              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">{t('Language')}</span>
                <div className="flex space-x-2">
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('fr')}
                  >
                    <span className="text-xl" role="img" aria-label="Français">🇫🇷</span>
                  </button>
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('en')}
                  >
                    <span className="text-xl" role="img" aria-label="English">🇬🇧</span>
                  </button>
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('es')}
                  >
                    <span className="text-xl" role="img" aria-label="Español">🇪🇸</span>
                  </button>
                </div>
              </div>

              <button
                onClick={logout}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
                {t('Logout')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderSettingsMenu = () => (
    <div className="relative">
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      </button>

      {isMenuOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
            <div className="py-1">
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">{t('Theme')}</span>
                <ThemeToggle onToggle={handleThemeToggle} />
              </div>

              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-700 dark:text-gray-200">{t('Language')}</span>
                <div className="flex space-x-2">
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('fr')}
                  >
                    <span className="text-xl" role="img" aria-label="Français">🇫🇷</span>
                  </button>
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('en')}
                  >
                    <span className="text-xl" role="img" aria-label="English">🇬🇧</span>
                  </button>
                  <button
                    className="hover:opacity-75 transition-opacity"
                    onClick={() => switchLanguage('es')}
                  >
                    <span className="text-xl" role="img" aria-label="Español">🇪🇸</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  if (isIframe)
    return null;

  // Rendu normal hors iframe
  return (
    <nav className={`p-2 shadow-md dark:bg-gray-800 dark:text-white dark:border-gray-700`}>
      <div className="mx-0 md:mx-2 flex justify-between items-center">
        <div className="flex items-center space-x-4 cursor-pointer" onClick={() => navigate('/')}>
          <img src={themeConfig.logo} alt="Logo" className="h-14" />
          {themeConfig.logo_client && (
            <img src={themeConfig.logo_client} alt="Client Logo" className="h-14" />
          )}
        </div>

        <div className="flex items-center space-x-3">
          {viewingAsClient && (
            <span className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-3 pr-1.5 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950/50 dark:text-amber-200">
              {t('Client view')}
              <button
                type="button"
                onClick={() => onToggleClientView(false)}
                title={t('Back to admin view')}
                className="rounded-full p-0.5 transition hover:bg-amber-200/70 dark:hover:bg-amber-900"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          )}
          {currentUser ? renderDropdownMenu() : renderSettingsMenu()}
        </div>
      </div>
    </nav>
  );
}

export default Topbar;
